import React from 'react';
import { Box, Unstable_Grid2 as Grid } from '@mui/material';
import dayjs from 'dayjs';

import {
  Button,
  Container,
  RowTypes,
  Select,
  StackedBarChart,
  Switch,
  Table,
  Text,
} from 'components';
import { downloadBlob } from 'core/shared';
import { useNavigate } from 'hooks/useNavigate';
import { replaceText, useTranslations } from 'hooks/useTranslations';

import { CustomString } from 'config/translations';
import { UseMutation, UseQuery } from 'useQuery';
import { MrrContribution, MrrForecastMonth, MrrMonth } from 'useQuery/types';

import { formatMonth, formatPeriod, MrrLinesDialog } from './MrrLinesDialog';

type RangeOption = '6' | '12' | '24';

const RANGE_OPTIONS: RangeOption[] = ['6', '12', '24'];

// Customers beyond this are folded into one "Other" bar so the chart stays
// readable; the tables below keep every line.
const CHART_CUSTOMERS = 8;

// A customer is one row of the breakdown; an organisation is identified by its
// id, a consumer only by the invoice lines behind it. Rows have to stay unique
// even when neither is there, so the position is the last resort.
function contributionId(contribution: MrrContribution, index: number) {
  return contribution.organisation_id ?? contribution.lines?.[0]?.invoice_line_id ?? `row-${index}`;
}

// MRR is not invoiced per month: it is the monthly share of every subscription
// invoice line, spread over the period that line covers. This tab shows that
// spread — per month, per customer, and down to the invoice lines behind it.
export function MrrStatistics() {
  const translations = useTranslations('statisticsPage');
  const { navigate } = useNavigate();

  const [range, setRange] = React.useState<RangeOption>('12');
  const [inclVat, setInclVat] = React.useState(true);
  // The toggle that turned the forecast on is gone, but everything behind it is
  // kept so it can be switched back on without rebuilding it.
  const [showForecast] = React.useState(false);
  const [selectedMonth, setSelectedMonth] = React.useState<string | undefined>(undefined);
  const [linesOf, setLinesOf] = React.useState<MrrContribution | null>(null);
  const [linesOpen, setLinesOpen] = React.useState(false);

  const { data: mrrData, isFetching } = UseQuery.mrrStatistics({ months: Number(range) });
  const { data: forecastData, isFetching: isFetchingForecast } = UseQuery.mrrForecast({
    months: Number(range),
  });
  const { mutateAsync: mutateAsyncExportMrr, isLoading: isExporting } =
    UseMutation.exportMrrStatistics();

  const byMonth = React.useMemo(() => mrrData?.result.by_month ?? [], [mrrData]);
  const forecastMonths = React.useMemo(
    () => (showForecast ? forecastData?.result.by_month ?? [] : []),
    [forecastData, showForecast],
  );

  const monthAmount = React.useCallback(
    (month: MrrMonth | MrrForecastMonth) => (inclVat ? month.mrr : month.mrr_net),
    [inclVat],
  );
  const contributionAmount = React.useCallback(
    (contribution: MrrContribution) => (inclVat ? contribution.amount : contribution.amount_net),
    [inclVat],
  );

  // Summary and breakdown both follow the selected month. Default to last
  // month: the running one is still being invoiced, so its share only tells
  // half a story. Fall back to the end of the range if it is not in there.
  const lastMonth = React.useMemo(
    () => dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
    [],
  );

  const activeIndex = React.useMemo(() => {
    const selected = byMonth.findIndex((month) => month.month === selectedMonth);
    if (selected !== -1) {
      return selected;
    }

    const previous = byMonth.findIndex((month) => month.month === lastMonth);

    return previous === -1 ? byMonth.length - 1 : previous;
  }, [byMonth, lastMonth, selectedMonth]);

  const activeMonth = byMonth[activeIndex];

  // One bar segment per customer, biggest first, with the tail as "Other".
  const chartSeries = React.useMemo(() => {
    const totals = new Map<string, number>();
    byMonth.forEach((month) => {
      month.contributions.forEach((contribution) => {
        totals.set(
          contribution.customer_name,
          (totals.get(contribution.customer_name) ?? 0) + contributionAmount(contribution),
        );
      });
    });

    const ranked = [...totals.entries()].sort(([, a], [, b]) => b - a);
    const shown = ranked.slice(0, CHART_CUSTOMERS).map(([name]) => name);

    function amountFor(month: MrrMonth, predicate: (name: string) => boolean) {
      return month.contributions
        .filter((contribution) => predicate(contribution.customer_name))
        .reduce((total, contribution) => total + contributionAmount(contribution), 0);
    }

    const series = shown.map((name) => ({
      label: name,
      values: byMonth.map((month) => amountFor(month, (customer) => customer === name) / 100),
    }));

    const otherValues = byMonth.map(
      (month) => amountFor(month, (customer) => !shown.includes(customer)) / 100,
    );

    const customerSeries = otherValues.some((value) => value > 0)
      ? [...series, { label: translations.mrrOther, values: otherValues }]
      : series;

    // Realised months carry no forecast and the other way around, so both
    // sides are padded with zeros to keep the bars aligned.
    return [
      ...customerSeries.map((item) => ({
        ...item,
        values: [...item.values, ...forecastMonths.map(() => 0)],
      })),
      ...(forecastMonths.length
        ? [
            {
              label: translations.mrrForecast,
              values: [
                ...byMonth.map(() => 0),
                ...forecastMonths.map((month) => monthAmount(month) / 100),
              ],
            },
          ]
        : []),
    ];
  }, [byMonth, contributionAmount, forecastMonths, monthAmount, translations]);

  return (
    <Box sx={{ padding: 1, width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 3,
          paddingTop: 2,
          paddingBottom: 2,
        }}
      >
        <Box sx={{ width: 220 }}>
          <Select
            mode="single"
            label={translations.mrrRange}
            value={range}
            options={RANGE_OPTIONS.map((option) => ({
              id: option,
              label: replaceText({ text: translations.mrrRangeMonths, replaceValues: [option] }),
            }))}
            onChange={setRange}
            fullWidth
          />
        </Box>
        <Switch label={translations.mrrInclVat} checked={inclVat} onChange={setInclVat} compact />
      </Box>

      <Grid container spacing={2}>
        <Container title={translations.mrrByCustomer} width="flex" loading={isFetching}>
          <StackedBarChart
            labels={[...byMonth, ...forecastMonths].map((month) => formatMonth(month.month))}
            data={chartSeries}
          />
        </Container>

        <Container title={translations.mrrPerMonth} width="small" loading={isFetching}>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ paddingLeft: 1, paddingBottom: 1 }}>
              <Text.Small color="gray">{translations.mrrSelectMonthHint}</Text.Small>
            </Box>
            <Table
              columns={{
                month: { name: translations.month, type: 'text', flex: 1 },
                mrr: { name: translations.mrr, type: 'price', flex: 1 },
                customers: { name: translations.mrrCustomers, type: 'text', width: 110 },
              }}
              // Most recent month first; the chart keeps running left to right.
              rows={[...byMonth].reverse().map((month) => ({
                id: month.month,
                month: formatMonth(month.month),
                mrr: monthAmount(month),
                customers: month.customer_count.toString(),
              }))}
              onClickRow={({ id }) => {
                setSelectedMonth(id);
              }}
              loading={isFetching}
              useMobileView={false}
            />
          </Box>
        </Container>

        {forecastMonths.length > 0 && (
          <Container
            title={
              replaceText({
                text: translations.mrrForecastMonths,
                replaceValues: [range],
              }) as CustomString
            }
            width="half"
            loading={isFetchingForecast}
          >
            <Box sx={{ width: '100%' }}>
              <Table
                columns={{
                  month: { name: translations.month, type: 'text', flex: 1 },
                  mrr: { name: translations.mrr, type: 'price', flex: 1 },
                  customers: { name: translations.mrrCustomers, type: 'text', width: 110 },
                  lines: { name: translations.mrrLines, type: 'text', width: 80 },
                }}
                rows={forecastMonths.map((month) => ({
                  id: month.month,
                  month: formatMonth(month.month),
                  mrr: monthAmount(month),
                  customers: month.customer_count.toString(),
                  // Contracts are rows, the projected lines behind them are not
                  lines: month.contributions
                    .reduce((total, contribution) => total + (contribution.lines?.length ?? 0), 0)
                    .toString(),
                }))}
                loading={isFetchingForecast}
                useMobileView={false}
              />
              <Box sx={{ paddingLeft: 1, paddingTop: 1 }}>
                <Text.Small color="gray">{translations.mrrForecastExplainer}</Text.Small>
              </Box>
            </Box>
          </Container>
        )}

        <Container
          title={
            replaceText({
              text: translations.mrrBreakdownOf,
              replaceValues: [activeMonth ? formatMonth(activeMonth.month) : ''],
            }) as CustomString
          }
          width="medium"
          loading={isFetching}
          headerAction={
            <Button
              size="small"
              label={translations.exportCSV}
              disabled={!activeMonth || isExporting}
              loading={isExporting}
              onClick={async () => {
                if (!activeMonth) {
                  return;
                }

                const { blob, fileName } = await mutateAsyncExportMrr({
                  month: activeMonth.month,
                });

                downloadBlob({ blob, fileName });
              }}
            />
          }
        >
          <Box sx={{ width: '100%' }}>
            <Table
              columns={{
                customer: { name: translations.mrrCustomer, type: 'text', flex: 1 },
                description: { name: translations.mrrDescription, type: 'text', flex: 1 },
                share: { name: translations.mrrShare, type: 'price', width: 140 },
                period: { name: translations.period, type: 'text', width: 190 },
                lines: { name: translations.mrrLines, type: 'buttonOrSVGOrEmpty', width: 170 },
              }}
              rows={(activeMonth?.contributions ?? []).map((contribution, index) => ({
                id: contributionId(contribution, index),
                customer: contribution.customer_name,
                description: contribution.description,
                share: contributionAmount(contribution),
                // Lines covering different periods have no single one to show
                period:
                  contribution.period_start && contribution.period_end
                    ? formatPeriod(contribution.period_start, contribution.period_end)
                    : translations.mrrMultiplePeriods,
                // Nothing to open when the share carries no lines
                lines: contribution.lines?.length
                  ? ({
                      variant: 'outlined',
                      label: translations.mrrSeeLines,
                      onClick: () => {
                        setLinesOf(contribution);
                        setLinesOpen(true);
                      },
                    } as RowTypes['buttonOrSVGOrEmpty'])
                  : undefined,
              }))}
              onClickRow={({ id, metaKey }) => {
                const contribution = (activeMonth?.contributions ?? []).find(
                  (item, index) => contributionId(item, index) === id,
                );

                if (contribution?.organisation_id) {
                  navigate({
                    name: 'organisation',
                    id: contribution.organisation_id,
                    searchParams: { tab: 1 },
                    metaKey,
                  });
                }
              }}
              loading={isFetching}
              useMobileView={false}
            />
          </Box>
        </Container>

        <MrrLinesDialog
          contribution={linesOf}
          month={activeMonth?.month}
          inclVat={inclVat}
          open={linesOpen}
          onClose={() => {
            setLinesOpen(false);
          }}
        />
      </Grid>
    </Box>
  );
}
